'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import type { Swiper as SwiperInstance } from 'swiper';
import CustomImage from '@/components/ui/CustomImage';
import {
  BANNER_AUTOPLAY_DELAY_MS,
  BANNER_VIEW_THRESHOLD,
  getBannerImageSrc,
} from '@/constants/banner';
import { useBanner, useClickBanner } from '@/lib/api';
import {
  isBannerMostlyVisible,
  trackBannerImpression,
} from '@/lib/utils/banner-view';
import type { BannerLayoutType } from '@/types/banner';
import type { BannerListDto } from '@/shared/banner';
import 'swiper/css';

type SiteBannerProps = {
  positionId: number;
  layout?: BannerLayoutType;
  className?: string;
  fillHeight?: boolean;
};

function SiteBannerSlide({
  banner,
  onClick,
  fillHeight = false,
}: {
  banner: BannerListDto;
  onClick: (banner: BannerListDto) => void;
  fillHeight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= BANNER_VIEW_THRESHOLD) {
          trackBannerImpression(banner.id);
        }
      },
      { threshold: BANNER_VIEW_THRESHOLD }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [banner.id]);

  return (
    <div
      ref={ref}
      data-banner-id={banner.id}
      onClick={() => onClick(banner)}
      className={`cursor-pointer ${fillHeight ? 'h-full' : ''}`}
    >
      <CustomImage
        src={getBannerImageSrc(banner.file)}
        alt={banner.title || 'banner'}
        width={banner.width ?? 0}
        height={banner.height ?? 0}
        className={fillHeight ? 'w-full h-full object-cover' : 'w-full h-auto'}
      />
    </div>
  );
}

export default function SiteBanner({
  positionId,
  layout = 'horizontal',
  className,
  fillHeight = false,
}: SiteBannerProps) {
  const { data: bannerData, isLoading } = useBanner(positionId);
  const { mutate: clickCount } = useClickBanner();
  const banners = useMemo(() => bannerData?.data ?? [], [bannerData?.data]);

  const handleClick = useCallback(
    (banner: BannerListDto) => {
      if (banner.isClick && banner.url) {
        clickCount(banner.id);
        window.open(banner.url, '_blank');
      }
    },
    [clickCount]
  );

  const trackActiveSlide = useCallback(
    (swiper: SwiperInstance) => {
      const banner = banners[swiper.realIndex];
      const slide = swiper.slides[swiper.activeIndex];
      const element = slide?.querySelector('[data-banner-id]');

      if (!banner || !element) {
        return;
      }

      if (isBannerMostlyVisible(element)) {
        trackBannerImpression(banner.id);
      }
    },
    [banners]
  );

  if (isLoading || banners.length === 0) {
    return null;
  }

  const canLoop = banners.length > 1;
  const firstBanner = banners[0];

  return (
    <div
      className={className}
      style={{
        width:
          layout === 'horizontal'
            ? '100%'
            : firstBanner.width
              ? `${firstBanner.width}px`
              : 'auto',
        height:
          layout === 'horizontal'
            ? 'auto'
            : firstBanner.height
              ? `${firstBanner.height}px`
              : 'auto',
      }}
    >
      <Swiper
        modules={[Autoplay]}
        slidesPerView={1}
        slidesPerGroup={1}
        autoplay={
          canLoop
            ? {
                delay: BANNER_AUTOPLAY_DELAY_MS,
                pauseOnMouseEnter: true,
              }
            : false
        }
        loop={canLoop}
        className={`relative w-full overflow-hidden ${fillHeight ? 'h-full' : ''}`}
        onSwiper={(swiper) => {
          requestAnimationFrame(() => trackActiveSlide(swiper));
        }}
        onSlideChangeTransitionEnd={trackActiveSlide}
      >
        {banners.map((banner, index) => (
          <SwiperSlide key={`${banner.id}-${index}`}>
            <SiteBannerSlide
              banner={banner}
              onClick={handleClick}
              fillHeight={fillHeight}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
